import { Graphics, Sprite, Text, Texture } from 'pixi.js';
import { GlowFilter } from 'pixi-filters';
import { STATION_ZONES } from '@astra/shared';
import type { EntityType, SelectionInfo } from '@astra/shared';
import { assetLoader } from '../core/AssetLoader';
import { TargetableEntity } from './base/TargetableEntity';
import { getStationImpostorTexture, bakeStationImpostor } from './StationImpostorBaker';

// Re-export for convenience
export { STATION_ZONES };

// Station color theme
const STATION_COLOR = 0x20a5a6;      // Teal (primary)
const SHIELD_COLOR = 0x44aaff;       // Blue for force field

// Impostor texture size (must match StationImpostorBaker.ts)
const IMPOSTOR_SIZE = 1024;

// Desired visual diameter for the station sprite in world units
const STATION_VISUAL_DIAMETER = 4500;

/**
 * Preload station impostor textures — call during boot.
 * Replaces the old SVG-based preloadStationSVGs().
 */
export async function preloadStationSVGs(): Promise<void> {
  await bakeStationImpostor('20a5a6');
}

// Fallback texture generation (procedural hexagon station)
function generateFallbackTexture(): Texture {
  const graphics = new Graphics();
  const size = 320;
  const center = size / 2;

  // Outer hexagon frame
  const outerPoints: number[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    outerPoints.push(center + Math.cos(angle) * 120);
    outerPoints.push(center + Math.sin(angle) * 120);
  }
  graphics.poly(outerPoints, true);
  graphics.stroke({ width: 3, color: STATION_COLOR, alpha: 1 });

  // Inner hexagon
  const innerPoints: number[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    innerPoints.push(center + Math.cos(angle) * 80);
    innerPoints.push(center + Math.sin(angle) * 80);
  }
  graphics.poly(innerPoints, true);
  graphics.stroke({ width: 2, color: STATION_COLOR, alpha: 0.8 });

  // Core
  graphics.circle(center, center, 40);
  graphics.stroke({ width: 2, color: STATION_COLOR, alpha: 0.9 });

  // Docking ports
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i;
    const px = center + Math.cos(angle) * 120;
    const py = center + Math.sin(angle) * 120;
    graphics.circle(px, py, 12);
    graphics.stroke({ width: 2, color: STATION_COLOR, alpha: 0.8 });
  }

  const texture = assetLoader.generateTexture(graphics, 'station-fallback');
  graphics.destroy();
  return texture;
}

let fallbackTexture: Texture | null = null;

export class Station extends TargetableEntity {
  // TargetableEntity abstract properties
  readonly id: string;
  readonly entityType: EntityType = 'station';
  readonly selectionRadius: number = 150;
  readonly isHostile: boolean = false;

  // Station-specific properties
  public readonly stationId: string;
  public readonly stationName: string;
  public readonly stationVariant: string;

  private stationSprite: Sprite;
  private zoneGraphics: Graphics;
  private shieldGraphics: Graphics;
  private pulseTime: number = 0;
  private shieldPulseTime: number = 0;
  private nameText: Text;
  private glowFilter: GlowFilter | null = null;

  // Internal angle in degrees
  private _angle: number = 0;

  // Optional tint override applied to the sprite + glow filter.
  // null = use default STATION_COLOR.
  private tintOverride: number | null = null;

  constructor(
    x: number,
    y: number,
    stationId: string,
    stationType: string = 'station_1',
    stationName: string = 'Station',
    tintOverride: number | null = null
  ) {
    super();
    this.tintOverride = tintOverride;

    this.position.set(x, y);
    this.stationId = stationId;
    this.id = stationId;
    this.stationName = stationName;

    // Always use the single station variant
    this.stationVariant = 'station';

    // Enable sorting by zIndex
    this.sortableChildren = true;

    // Create zone graphics (drawn ONCE, not every frame)
    this.zoneGraphics = new Graphics();
    this.zoneGraphics.zIndex = 0;
    this.addChild(this.zoneGraphics);
    this.drawZones();

    // Create animated shield graphics
    this.shieldGraphics = new Graphics();
    this.shieldGraphics.zIndex = 1;
    this.addChild(this.shieldGraphics);

    // Create station sprite (will be set in initSprite)
    this.stationSprite = new Sprite();
    this.stationSprite.anchor.set(0.5);
    this.stationSprite.zIndex = 2;
    this.addChild(this.stationSprite);

    // Try to use impostor texture, fallback to procedural
    this.initSprite();

    // Create station name label
    this.nameText = new Text({
      text: this.stationName.toUpperCase(),
      style: {
        fontFamily: 'monospace',
        fontSize: 14,
        fill: STATION_COLOR,
        stroke: { color: 0x0a1628, width: 2 },
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.position.set(0, -180);
    this.nameText.zIndex = 4;
    this.addChild(this.nameText);
  }

  private initSprite(): void {
    // Try the pre-baked impostor texture (already baked at boot)
    const texture = getStationImpostorTexture('20a5a6');

    if (texture) {
      this.stationSprite.texture = texture;

      // Scale to desired world size
      const scale = STATION_VISUAL_DIAMETER / IMPOSTOR_SIZE;
      this.stationSprite.scale.set(scale);

      // No tint on the impostor — the 3D render already has correct colors.
      // But we still apply GlowFilter for the holographic station feel.
      const glowColor = this.tintOverride ?? STATION_COLOR;
      this.glowFilter = new GlowFilter({
        distance: 12,
        outerStrength: 0.5,
        innerStrength: 0.15,
        color: glowColor,
        quality: 0.3,
      });
      this.stationSprite.filters = [this.glowFilter];
    } else {
      // Fallback to procedural hexagon
      if (!fallbackTexture) {
        fallbackTexture = generateFallbackTexture();
      }
      this.stationSprite.texture = fallbackTexture;
    }
  }

  // TargetableEntity abstract method implementation
  getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this.id,
      name: this.stationName,
      x: this.x,
      y: this.y,
      data: {
        dockingRadius: STATION_ZONES.DOCKING_RADIUS,
        safeZoneRadius: STATION_ZONES.SAFE_ZONE_RADIUS,
      },
    };
  }

  // Draw zones ONCE in constructor
  private drawZones(): void {
    // Safe zone outer edge
    this.drawDashedCircle(0, 0, STATION_ZONES.SAFE_ZONE_RADIUS, 30, 15, 2, SHIELD_COLOR, 0.4);

    // Safe zone inner glow line
    this.zoneGraphics.circle(0, 0, STATION_ZONES.SAFE_ZONE_RADIUS - 10);
    this.zoneGraphics.stroke({ width: 1, color: SHIELD_COLOR, alpha: 0.2 });

    // Safe zone fill
    this.zoneGraphics.circle(0, 0, STATION_ZONES.SAFE_ZONE_RADIUS);
    this.zoneGraphics.fill({ color: SHIELD_COLOR, alpha: 0.03 });

    // Docking zone
    this.drawDashedCircle(0, 0, STATION_ZONES.DOCKING_RADIUS, 20, 10, 2, STATION_COLOR, 0.4);
    this.zoneGraphics.circle(0, 0, STATION_ZONES.DOCKING_RADIUS);
    this.zoneGraphics.fill({ color: STATION_COLOR, alpha: 0.06 });

    // Zone markers
    this.drawZoneMarkers();
  }

  private drawZoneMarkers(): void {
    const radius = STATION_ZONES.SAFE_ZONE_RADIUS;
    const markerCount = 8;

    for (let i = 0; i < markerCount; i++) {
      const angle = (Math.PI * 2 / markerCount) * i;
      const cx = Math.cos(angle) * radius;
      const cy = Math.sin(angle) * radius;
      const size = 8;

      this.zoneGraphics.poly([
        cx, cy - size,
        cx + size, cy,
        cx, cy + size,
        cx - size, cy
      ], true);
      this.zoneGraphics.stroke({ width: 2, color: SHIELD_COLOR, alpha: 0.5 });

      const tickInner = radius - 20;
      const ix = Math.cos(angle) * tickInner;
      const iy = Math.sin(angle) * tickInner;
      this.zoneGraphics.moveTo(cx, cy);
      this.zoneGraphics.lineTo(ix, iy);
      this.zoneGraphics.stroke({ width: 1, color: SHIELD_COLOR, alpha: 0.3 });
    }
  }

  private drawShieldEffect(): void {
    this.shieldGraphics.clear();

    const radius = STATION_ZONES.SAFE_ZONE_RADIUS;
    const arcCount = 6;
    const arcLength = Math.PI / 8;
    const rotationOffset = this.shieldPulseTime * 0.3;

    for (let i = 0; i < arcCount; i++) {
      const baseAngle = (Math.PI * 2 / arcCount) * i + rotationOffset;
      const pulse = Math.sin(this.shieldPulseTime * 2 + i) * 0.3 + 0.7;

      this.shieldGraphics.arc(0, 0, radius - 5, baseAngle, baseAngle + arcLength);
      this.shieldGraphics.stroke({ width: 3, color: SHIELD_COLOR, alpha: 0.15 * pulse });

      this.shieldGraphics.arc(0, 0, radius - 15, baseAngle + arcLength * 0.2, baseAngle + arcLength * 0.8);
      this.shieldGraphics.stroke({ width: 2, color: SHIELD_COLOR, alpha: 0.1 * pulse });
    }

    const glowPulse = Math.sin(this.shieldPulseTime * 1.5) * 0.5 + 0.5;
    this.shieldGraphics.circle(0, 0, radius);
    this.shieldGraphics.stroke({ width: 4, color: SHIELD_COLOR, alpha: 0.05 * glowPulse });
  }

  private drawDashedCircle(
    cx: number,
    cy: number,
    radius: number,
    dashLength: number,
    gapLength: number,
    lineWidth: number,
    color: number,
    alpha: number
  ): void {
    const circumference = 2 * Math.PI * radius;
    const totalSegments = Math.floor(circumference / (dashLength + gapLength));
    const anglePerSegment = (2 * Math.PI) / totalSegments;
    const dashAngle = (dashLength / circumference) * 2 * Math.PI;

    for (let i = 0; i < totalSegments; i++) {
      const startAngle = i * anglePerSegment;
      const endAngle = startAngle + dashAngle;

      this.zoneGraphics.arc(cx, cy, radius, startAngle, endAngle);
      this.zoneGraphics.stroke({ width: lineWidth, color, alpha });
    }
  }

  // Check if a point is within the safe zone
  isInSafeZone(x: number, y: number): boolean {
    const dx = x - this.x;
    const dy = y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < STATION_ZONES.SAFE_ZONE_RADIUS;
  }

  // Check if a point is within docking range
  isInDockingRange(x: number, y: number): boolean {
    const dx = x - this.x;
    const dy = y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < STATION_ZONES.DOCKING_RADIUS;
  }

  // Get distance to station center
  getDistanceTo(x: number, y: number): number {
    const dx = x - this.x;
    const dy = y - this.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Get station's current rotation in degrees
  getRotation(): number {
    return this._angle;
  }

  update(delta: number): void {
    this.pulseTime += delta / 1000;
    this.shieldPulseTime += delta / 1000;

    // Update animated shield effect
    this.drawShieldEffect();
  }

  destroy(): void {
    this.stationSprite.destroy();
    this.zoneGraphics.destroy();
    this.shieldGraphics.destroy();
    this.nameText.destroy();
    super.destroy();
  }

  // Get position for radar display
  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }
}
